import { useCallback, useEffect, useRef, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { notifyVeranstaltungBadgeRefresh } from "@/lib/navBadges";
import { toast } from "@/lib/toast";
import {
  getVeranstaltungScanJob,
  listVeranstaltungMails,
  markVeranstaltungMail,
  reanalyzeVeranstaltungMail,
  scanVeranstaltungMailbox,
  VERANSTALTUNG_SCAN_JOB_STORAGE_KEY,
  type VeranstaltungCounts,
  type VeranstaltungFilter,
  type VeranstaltungKontakt,
  type VeranstaltungMail,
  type VeranstaltungScanJob,
  type VeranstaltungVersionHinweis,
} from "@/lib/veranstaltungen";
import { einsatzMerosZustand, type MerosZustand } from "@/lib/meros";
import { fmtDateTime } from "@/pages/anfragen/anfrageWizardUtils";
import { useNewProject } from "@/pages/projekte/useNewProject";
import { MerosZustandBadge } from "@/pages/veranstaltungen/MerosZustandBadge";
import { RegieprotokollPdfDialog } from "@/pages/veranstaltungen/RegieprotokollPdfDialog";

const POLL_INTERVAL_MS = 20_000;
const SCAN_POLL_INTERVAL_MS = 1500;

const FILTERS: { id: VeranstaltungFilter; label: string }[] = [
  { id: "alle", label: "Alle" },
  { id: "neu", label: "Neu" },
  { id: "mit_einsatz", label: "Mit Einsatz" },
  { id: "ohne", label: "Ohne Einsatz" },
  { id: "gesehen", label: "Gesehen" },
];

function merosZustaende(row: VeranstaltungMail): MerosZustand[] {
  const seen: MerosZustand[] = [];
  for (const einsatz of row.einsaetze) {
    const zustand = einsatzMerosZustand(einsatz);
    if (zustand && !seen.includes(zustand)) seen.push(zustand);
  }
  return seen;
}

function scanJobIsActive(job: VeranstaltungScanJob | null): boolean {
  return job?.status === "queued" || job?.status === "running";
}

function senderLabel(row: VeranstaltungMail): string {
  const name = (row.from_name || "").trim();
  const email = (row.from_email || "").trim();
  if (name && email) return `${name} <${email}>`;
  return name || email || "Unbekannter Absender";
}

function placeLabel(bereich: string, ort: string): string {
  if (bereich && ort && bereich !== ort) return `${bereich} · ${ort}`;
  return bereich || ort || "—";
}

function contactBits(contact: VeranstaltungKontakt | undefined): string {
  if (!contact) return "";
  return [contact.abteilung, contact.telefon ? `Tel. ${contact.telefon}` : "", contact.mobil ? `Mobil ${contact.mobil}` : "", contact.email]
    .filter(Boolean)
    .join(" · ");
}

function einsatzKurzLabel(row: { bereich?: string; datum?: string; aufgabe?: string; notiz?: string }): string {
  const main = [row.bereich, row.datum, row.aufgabe].filter(Boolean).join(" · ");
  return row.notiz ? `${main} — ${row.notiz}` : main;
}

function VersionsHinweis({ hinweis }: { hinweis: VeranstaltungVersionHinweis }) {
  const andere = hinweis.andere_version ? `Version ${hinweis.andere_version}` : "einer älteren Fassung";
  if (hinweis.art === "ersetzt") {
    return (
      <p className="mt-2 text-sm text-label-secondary">
        Ersetzt durch {hinweis.andere_version ? `Version ${hinweis.andere_version}` : "eine neuere Fassung"}.
      </p>
    );
  }
  const neu = hinweis.neu || [];
  const entfernt = hinweis.entfernt || [];
  return (
    <div className="mt-2 text-sm">
      <p>
        {hinweis.version ? `Version ${hinweis.version}` : "Diese Fassung"} löst {andere} ab.
        {hinweis.unveraendert ? " Die Einsätze sind unverändert." : ""}
      </p>
      {entfernt.length > 0 ? (
        <p className="mt-1 text-label-secondary">
          Entfallen: {entfernt.map(einsatzKurzLabel).join("; ")}
        </p>
      ) : null}
      {neu.length > 0 ? (
        <p className="mt-1 text-label-secondary">Neu: {neu.map(einsatzKurzLabel).join("; ")}</p>
      ) : null}
    </div>
  );
}

function ProjektVorschlag({ row, onCreate }: { row: VeranstaltungMail; onCreate: () => void }) {
  const details = row.details;
  const projekt = details?.projekt;
  const partner = details?.ansprechpartner;
  const veranstalter = details?.veranstalter;
  const leiter = (details?.kontakte || []).find((item) => item.rolle === "Veranstaltungsleiter" && item.name);
  const hasProject =
    Boolean(projekt?.beschreibung) || Boolean(partner?.name) || Boolean(details?.kunde);
  if (!hasProject) return null;
  return (
    <div className="border-t border-separator px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1 text-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-label-secondary">Für das Projekt</p>
          {details?.kunde ? (
            <p>
              <span className="text-label-secondary">Kunde </span>
              {details.kunde}
              {details.kundennummer ? ` · Kundennr. ${details.kundennummer}` : ""}
              {details.angebot ? ` · Angebot ${details.angebot}` : ""}
            </p>
          ) : null}
          {partner?.name ? (
            <p>
              <span className="text-label-secondary">Ansprechpartner </span>
              {partner.name}
              {contactBits(partner) ? <span className="text-label-secondary"> · {contactBits(partner)}</span> : null}
            </p>
          ) : null}
          {partner?.hinweis ? <p className="text-xs text-label-secondary">{partner.hinweis}</p> : null}
          {veranstalter?.firma || veranstalter?.name ? (
            <p>
              <span className="text-label-secondary">Veranstalter </span>
              {[veranstalter.firma, veranstalter.name].filter(Boolean).join(" · ")}
              {contactBits(veranstalter) ? <span className="text-label-secondary"> · {contactBits(veranstalter)}</span> : null}
            </p>
          ) : null}
          {leiter ? (
            <p>
              <span className="text-label-secondary">Veranstaltungsleiter </span>
              {[leiter.name, leiter.firma].filter(Boolean).join(" · ")}
              {contactBits(leiter) ? <span className="text-label-secondary"> · {contactBits(leiter)}</span> : null}
            </p>
          ) : null}
          {projekt?.beschreibung ? (
            <p>
              <span className="text-label-secondary">Beschreibung </span>
              {projekt.beschreibung}
            </p>
          ) : null}
        </div>
        <Button size="sm" onClick={onCreate}>
          Projekt anlegen
        </Button>
      </div>
    </div>
  );
}

function openProjectFromMail(
  row: VeranstaltungMail,
  openNewProject: ReturnType<typeof useNewProject>["open"],
) {
  const projekt = row.details?.projekt;
  const partner = row.details?.ansprechpartner;
  openNewProject({
    formPrefill: {
      beschreibung: projekt?.beschreibung || row.event_title || "",
      ansprechpartner: projekt?.ansprechpartner || partner?.name || "",
      ansprechpartnerEmail: projekt?.ansprechpartner_email || partner?.email || "",
    },
  });
}

export function VeranstaltungenIndexPage() {
  const [items, setItems] = useState<VeranstaltungMail[]>([]);
  const [counts, setCounts] = useState<VeranstaltungCounts>({
    total: 0,
    neu: 0,
    gesehen: 0,
    mit_einsatz: 0,
    ohne: 0,
    offene_einsaetze: 0,
  });
  const [mailbox, setMailbox] = useState("veranstaltungen@ernsthaus.de");
  const [liveMonitor, setLiveMonitor] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [filter, setFilter] = useState<VeranstaltungFilter>("alle");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scanSinceDate, setScanSinceDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().slice(0, 10);
  });
  const [scanning, setScanning] = useState(false);
  const [scanJob, setScanJob] = useState<VeranstaltungScanJob | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [pdfPreview, setPdfPreview] = useState<{
    mailId: number;
    attachmentId: string;
    name: string;
  } | null>(null);
  const knownUnread = useRef<number | null>(null);
  const { open: openNewProject } = useNewProject();

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const response = await listVeranstaltungMails(filter);
        setItems(response.items || []);
        setCounts(response.counts);
        const open = Number(response.counts?.offene_einsaetze || 0);
        if (knownUnread.current !== null && knownUnread.current !== open) {
          notifyVeranstaltungBadgeRefresh();
        }
        knownUnread.current = open;
        setMailbox(response.mailbox || "veranstaltungen@ernsthaus.de");
        setLiveMonitor(Boolean(response.live_monitor));
        setLastError(response.last_error);
      } catch (err) {
        const message = err instanceof ApiError ? err.message : "Die Mails konnten nicht geladen werden.";
        if (!silent) setError(message);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [filter],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      void load(true);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  useEffect(() => {
    const stored = sessionStorage.getItem(VERANSTALTUNG_SCAN_JOB_STORAGE_KEY);
    if (!stored) return;
    void getVeranstaltungScanJob(stored)
      .then((job) => {
        if (scanJobIsActive(job)) setScanJob(job);
        else sessionStorage.removeItem(VERANSTALTUNG_SCAN_JOB_STORAGE_KEY);
      })
      .catch(() => sessionStorage.removeItem(VERANSTALTUNG_SCAN_JOB_STORAGE_KEY));
  }, []);

  useEffect(() => {
    if (!scanJob || !scanJobIsActive(scanJob)) return;
    const id = window.setInterval(() => {
      void getVeranstaltungScanJob(scanJob.job_id)
        .then((job) => {
          setScanJob(job);
          if (!scanJobIsActive(job)) {
            sessionStorage.removeItem(VERANSTALTUNG_SCAN_JOB_STORAGE_KEY);
            void load(true);
            if (job.status === "completed") toast.success(job.message || "Postfach gelesen.");
            if (job.status === "failed") toast.error(job.error || "Analyse fehlgeschlagen.");
          }
        })
        .catch(() => undefined);
    }, SCAN_POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [scanJob, load]);

  async function handleScan() {
    setScanning(true);
    try {
      const response = await scanVeranstaltungMailbox(scanSinceDate);
      sessionStorage.setItem(VERANSTALTUNG_SCAN_JOB_STORAGE_KEY, response.job_id);
      setScanJob(await getVeranstaltungScanJob(response.job_id));
      toast.success(response.message || "Analyse gestartet.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Scan fehlgeschlagen.");
    } finally {
      setScanning(false);
    }
  }

  async function handleSeen(row: VeranstaltungMail, seen: boolean) {
    setBusyId(row.id);
    try {
      await markVeranstaltungMail(row.id, seen);
      await load(true);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Status konnte nicht gespeichert werden.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleReanalyze(row: VeranstaltungMail) {
    setBusyId(row.id);
    try {
      await reanalyzeVeranstaltungMail(row.id);
      toast.success("Mail erneut gelesen.");
      await load(true);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Erneutes Lesen fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  }

  const scanInProgress = scanning || scanJobIsActive(scanJob);

  return (
    <AppShell
      title="Veranstaltungen Messe"
      subtitle={`${mailbox} – Regieprotokolle und Ernsthaus-Einsätze`}
    >
      <div className="space-y-4">
        <Alert variant="info">
          Das Postfach wird überwacht
          {liveMonitor ? " (sofort per Webhook und zusätzlich alle 10 Minuten)" : " (alle 10 Minuten)"}.
          In Regieprotokollen – PDF-Dateiname beginnt mit RP – werden Einsätze erkannt, bei denen
          in der Spalte Firma Ernsthaus oder eine Abkürzung wie Ernsth. steht. Eine Mail kann keinen,
          einen oder mehrere Einsätze enthalten.
        </Alert>

        {lastError ? <Alert variant="warning">{lastError}</Alert> : null}

        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-label-secondary">Mails lesen ab</span>
            <input
              type="date"
              className="rounded border border-separator bg-card px-3 py-1.5 text-sm"
              value={scanSinceDate}
              onChange={(event) => setScanSinceDate(event.target.value)}
            />
          </label>
          <Button onClick={() => void handleScan()} disabled={scanInProgress || !scanSinceDate}>
            {scanInProgress ? <Spinner className="h-3.5 w-3.5" /> : "Postfach ab Datum lesen"}
          </Button>
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            Aktualisieren
          </Button>
          <span className="text-sm text-label-secondary">
            Offene Einsätze: <strong>{counts.offene_einsaetze}</strong>
            <span className="mx-2">·</span>
            Mails mit Einsatz: <strong>{counts.mit_einsatz}</strong>
          </span>
        </div>

        {scanJob ? (
          <Card className="px-4 py-3">
            <p className="text-sm text-label-secondary">{scanJob.message || "Analyse läuft…"}</p>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-label-secondary/15">
              <div
                className="h-full rounded-full bg-accent transition-all duration-300"
                style={{ width: `${Math.min(100, Math.max(scanJob.total == null ? 30 : 0, scanJob.progress_percent || 0))}%` }}
              />
            </div>
          </Card>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((item) => {
            const count =
              item.id === "neu"
                ? counts.neu
                : item.id === "mit_einsatz"
                  ? counts.mit_einsatz
                  : item.id === "ohne"
                    ? counts.ohne
                    : item.id === "gesehen"
                      ? counts.gesehen
                      : counts.total;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setFilter(item.id)}
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  filter === item.id
                    ? "bg-accent text-white"
                    : "border border-separator text-label-secondary hover:bg-card"
                }`}
              >
                {item.label}
                {count > 0 ? ` (${count})` : ""}
              </button>
            );
          })}
        </div>

        {error ? <Alert variant="error">{error}</Alert> : null}

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <Card>
            <p className="px-4 py-8 text-center text-sm text-label-secondary">
              Keine Mails in dieser Ansicht. Neue Mails erscheinen automatisch, ältere über
              „Postfach ab Datum lesen“.
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((row) => {
              const marked = row.einsatz_count > 0;
              const ersetzt = row.version_hinweis?.art === "ersetzt";
              const rpFiles = (row.attachments_summary || []).filter((att) => att.is_rp);
              const meros = merosZustaende(row);
              return (
                <Card
                  key={row.id}
                  className={
                    ersetzt
                      ? "opacity-80"
                      : marked && row.status === "neu"
                        ? "border-status-orange/50 ring-1 ring-status-orange/30"
                        : undefined
                  }
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-base font-semibold text-label">
                          {row.event_title || row.subject || "(ohne Betreff)"}
                        </h2>
                        {marked ? (
                          <span className="rounded-full bg-status-orange/15 px-2 py-0.5 text-xs font-semibold text-status-orange">
                            {row.einsatz_count} {row.einsatz_count === 1 ? "Einsatz" : "Einsätze"}
                          </span>
                        ) : (
                          <span className="rounded-full bg-label-secondary/10 px-2 py-0.5 text-xs text-label-secondary">
                            Kein Einsatz
                          </span>
                        )}
                        {row.status === "gesehen" ? (
                          <span className="rounded-full bg-status-green/15 px-2 py-0.5 text-xs font-medium text-status-green">
                            Gesehen
                          </span>
                        ) : null}
                        {row.version_hinweis?.art === "aktualisierung" ? (
                          <span className="rounded-full bg-accent/15 px-2 py-0.5 text-xs font-semibold text-accent">
                            Aktualisierung
                          </span>
                        ) : null}
                        {ersetzt ? (
                          <span className="rounded-full bg-label-secondary/10 px-2 py-0.5 text-xs font-medium text-label-secondary">
                            Ersetzt
                          </span>
                        ) : null}
                        {meros.map((zustand) => (
                          <MerosZustandBadge key={zustand} zustand={zustand} />
                        ))}
                      </div>
                      <p className="mt-1 text-sm text-label-secondary">{senderLabel(row)}</p>
                      <p className="mt-1 text-xs text-label-secondary">
                        {row.received_at ? fmtDateTime(row.received_at) : "—"}
                        {row.event_date ? ` · ${row.event_date}` : ""}
                        {row.venue ? ` · ${row.venue}` : ""}
                        {row.event_version ? ` · ${row.event_version}` : ""}
                        {row.rp_nummer ? ` · RP ${row.rp_nummer}` : ""}
                        {row.psp ? ` · PSP ${row.psp}` : ""}
                      </p>
                      {row.subject && row.event_title && row.subject !== row.event_title ? (
                        <p className="mt-1 truncate text-xs text-label-secondary">{row.subject}</p>
                      ) : null}
                      {row.version_hinweis ? <VersionsHinweis hinweis={row.version_hinweis} /> : null}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {marked && row.status !== "gesehen" ? (
                        <Button size="sm" variant="secondary" disabled={busyId === row.id} onClick={() => void handleSeen(row, true)}>
                          Als gesehen markieren
                        </Button>
                      ) : null}
                      {row.status === "gesehen" ? (
                        <Button size="sm" variant="ghost" disabled={busyId === row.id} onClick={() => void handleSeen(row, false)}>
                          Wieder öffnen
                        </Button>
                      ) : null}
                      <Button size="sm" variant="ghost" disabled={busyId === row.id} onClick={() => void handleReanalyze(row)}>
                        Erneut lesen
                      </Button>
                    </div>
                  </div>

                  {(marked || row.has_rp) ? (
                    <ProjektVorschlag row={row} onCreate={() => openProjectFromMail(row, openNewProject)} />
                  ) : null}

                  {marked ? (
                    <div className="overflow-x-auto border-t border-separator">
                      <table className="min-w-full text-left text-sm">
                        <thead className="bg-status-orange/10 text-xs uppercase tracking-wide text-label-secondary">
                          <tr>
                            <th className="px-4 py-2 font-medium">Ort</th>
                            <th className="px-4 py-2 font-medium">Was</th>
                            <th className="px-4 py-2 font-medium">Wann</th>
                            <th className="px-4 py-2 font-medium">Aufgabe</th>
                            <th className="px-4 py-2 font-medium">Abt.</th>
                            <th className="px-4 py-2 font-medium">Firma</th>
                          </tr>
                        </thead>
                        <tbody>
                          {row.einsaetze.map((einsatz, index) => {
                            const merosZustand = einsatzMerosZustand(einsatz);
                            return (
                              <tr key={`${row.id}-${index}`} className="border-t border-status-orange/20 bg-status-orange/10">
                                <td className="px-4 py-2 align-top">{placeLabel(einsatz.bereich, einsatz.ort)}</td>
                                <td className="px-4 py-2 align-top">{einsatz.kategorie || "—"}</td>
                                <td className="whitespace-nowrap px-4 py-2 align-top">{einsatz.datum || "—"}</td>
                                <td className="px-4 py-2 align-top">
                                  {merosZustand ? (
                                    <div className="mb-1">
                                      <MerosZustandBadge zustand={merosZustand} />
                                    </div>
                                  ) : null}
                                  <div>{einsatz.aufgabe || "—"}</div>
                                  {einsatz.notiz ? (
                                    <div className="mt-1 whitespace-pre-line text-xs leading-5 text-label-secondary">
                                      {einsatz.notiz}
                                    </div>
                                  ) : null}
                                </td>
                                <td className="px-4 py-2 align-top">{einsatz.abteilung || "—"}</td>
                                <td className="px-4 py-2 align-top font-semibold text-status-orange">
                                  {einsatz.firma}
                                  {einsatz.seite ? (
                                    <span className="ml-2 font-normal text-label-secondary">S. {einsatz.seite}</span>
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="border-t border-separator px-4 py-3 text-sm text-label-secondary">
                      {row.has_rp
                        ? "Regieprotokoll vorhanden, darin kein Ernsthaus-Einsatz."
                        : "Kein Regieprotokoll in dieser Mail."}
                    </p>
                  )}

                  {rpFiles.length > 0 ? (
                    <div className="flex flex-wrap gap-2 border-t border-separator px-4 py-3">
                      {rpFiles.map((att) => (
                        <button
                          key={att.id}
                          type="button"
                          className="rounded border border-separator px-2 py-1 text-xs text-label hover:bg-fill-tertiary"
                          onClick={() =>
                            setPdfPreview({
                              mailId: row.id,
                              attachmentId: att.id,
                              name: att.name || "Regieprotokoll",
                            })
                          }
                        >
                          {att.name || "Regieprotokoll"}
                          {att.parse_error ? " (Lesefehler)" : ""}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </Card>
              );
            })}
          </div>
        )}
      </div>
      <RegieprotokollPdfDialog
        open={pdfPreview != null}
        mailId={pdfPreview?.mailId ?? null}
        attachmentId={pdfPreview?.attachmentId ?? null}
        title={pdfPreview?.name || "Regieprotokoll"}
        onClose={() => setPdfPreview(null)}
      />
    </AppShell>
  );
}
